import {
  Box,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack
} from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import { AppState, useAppSelector } from 'src/store';
import DocManagementService from '../../documentUpload/service/DocManagementService';
import DocumentMetricComponent from './docMetricChart/DocumentMetricComponent';
import DocumentStatusOverTime from './DocumentStatusOverTime';

const DocumentStatusOverTimeComponent = (props: any) => {
  const DEFAULT_MAX_RECORDS_COUNT = 5;
  const { study, previousStudyValue, country, site } = props;
  const DOC_METRICS_OFFSET = 5;
  const docService = new DocManagementService();
  const docStatusOverTime = new DocumentStatusOverTime();
  const user: any = useAppSelector(state => state.user.user);
  const selectedStudy = useAppSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Study);
  const selectedCountry = useAppSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Country);
  const selectedSite = useAppSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Site);

  let TmfLevel =
    (selectedStudy === '--' || selectedStudy === '') &&
    (selectedCountry === '--' || selectedCountry == '') &&
    (selectedSite === '--' || selectedSite === '')
      ? '--'
      : (selectedCountry === '--' || selectedCountry == '') && (selectedSite === '--' || selectedSite === '')
      ? 'Study'
      : selectedSite === '--' && selectedCountry != '--'
      ? 'Country'
      : 'Site';
  if (TmfLevel === '--') {
    TmfLevel = 'Study';
  }

  let readyForApprovalMetrics: any = [];
  let approvedMetrics: any = [];
  let rejectedMetrics: any = [];
  let uploadedMetrics: any = [];
  let expectedMetrics: any = [];
  let monthsMetrics: any = [];

  const defaultDocType = [{ zone: '', tmf_document_type: 'All' }];
  const defaultDocSubType = [{ section: '', tmf_document_subtype: 'All' }];
  const defaultDocArtifactType = [{ tmf_artifact_number: '', tmf_artifact_name: 'All' }];
  const defaultMilestone = [{ tmf_doc_milestone: 'All' }];
  const defaultOwner = [{ id: '', firstName: 'All', lastName: '', username: 'All' }];

  const [loadingDocStatusOverTime, setLoadingDocStatusOverTime] = React.useState<any>(false);
  const [documentDetails, setDocumentDetails] = React.useState<any>();
  const [docTypes, setDocTypes] = React.useState<any>();
  const [selectedDocType, setSelectedDocType] = React.useState<any>();

  const [docSubTypes, setDocSubTypes] = React.useState<any>();
  const [selectedDocSubType, setSelectedDocSubType] = React.useState<any>();

  const [artifactNames, setArtifactNames] = React.useState<any>();
  const [selectedArtifactName, setSelectedArtifactName] = React.useState<any>();

  const [milestones, setMilestones] = React.useState<any>();
  const [selectedMilestone, setSelectedMilestone] = React.useState<any>();

  const [selectedOwner, setSelectedOwner] = React.useState<any>('All');
  const [ownersList, setOwnersList] = React.useState<any>();
  const [userLoading, setUserLoading] = React.useState<any>(false);
  const [masterlistLoading, setMasterlistLoading] = React.useState<any>(false);

  const [documentsStatusList, setDocumentsStatusList] = React.useState<any>([]);
  const [selectedDocStatus, setSelectedDocStatus] = React.useState<any>([]);

  const [documentSeriesMetrics, setDocumentSeriesMetrics] = React.useState<any>([]);
  const [timelineMetrics, setTimelineMetrics] = React.useState<any>([]);
  const [maxCountValue, setMaxCountValue] = React.useState(5);

  const isAllSelected = documentsStatusList.length > 0 && selectedDocStatus.length === documentsStatusList.length;

  /**
   * 1. Get all document details of level Type, sub type, artifacts.
   */
  const getDocumentDetails = async () => {
    setLoadingDocStatusOverTime(true);
    const params = {
      module_section_related: 'TMFLandingPage',
      parent_entity_related: 'DocumentStatuses'
    };
    const response: any = await docService.getBinderViewerTree(
      TmfLevel,
      selectedStudy,
      selectedCountry,
      selectedSite,
      params
    );
    if (response?.data) {
      const responseData = response.data || [];
      setDocumentDetails(responseData);
      setDocTypes(responseData);
    } else {
      setDocumentDetails(defaultDocType);
      setDocTypes(defaultDocType);
    }
    setLoadingDocStatusOverTime(false);
  };

  /**
   * 2. Fetches all milestone list.
   */
  const getMilestoneList = async () => {
    setMasterlistLoading(true);
    if (
      selectedDocType?.tmf_document_type &&
      selectedDocSubType?.tmf_document_subtype &&
      selectedArtifactName?.tmf_artifact_name
    ) {
      const tmfLevel = docService.getTmfLevelBy(study, country, site);
      const params = {
        module_section_related: 'TMFLandingPage',
        parent_entity_related: 'Milestonestatus'
      };
      const resp: any = await docService.getMilestoneList(
        tmfLevel,
        selectedDocType?.tmf_document_type,
        selectedDocSubType?.tmf_document_subtype,
        selectedArtifactName?.tmf_artifact_name,
        selectedStudy,
        selectedCountry,
        selectedSite,
        params
      );
      if (resp?.data) {
        const responseData: any = resp.data || [];
        const mileStonesList = [...defaultMilestone, ...responseData];
        setMilestones(mileStonesList);
        setSelectedMilestone(mileStonesList[0]);
      } else {
        setMilestones(defaultMilestone);
        setSelectedMilestone(defaultMilestone[0]);
      }
      setMasterlistLoading(false);
    }
  };

  /**
   * 3. Fetches all sys admin users.
   */
  const getUsersList = async () => {
    setUserLoading(true);
    const resp: any = await docService.getAllUsers(selectedStudy, TmfLevel, '--', selectedCountry, selectedSite);
    if (resp?.data) {
      const responseData: any = resp.data || [];
      const ownersArray = [...defaultOwner, ...responseData];
      setOwnersList(ownersArray);
      setSelectedOwner(ownersArray[0]);
    } else {
      setOwnersList(defaultOwner);
      setSelectedOwner(defaultOwner[0]);
    }
    setUserLoading(false);
  };

  /**
   * 4. Get document metrics for last five months.
   */
  const getDocumentMetricsRecords = async () => {
    const params = {
      module_section_related: 'TMFLandingPage',
      parent_entity_related: 'MetricsStatus'
    };
    const tmfLevel = docService.getTmfLevelBy(study, country, site);
    const statusList: any = {
      tmf_doc_status: selectedDocStatus.map((docStatus: any) => docStatus.name)
    };
    const status = selectedDocStatus.length > 0 ? statusList : { tmf_doc_status: ['None'] };
    const emptyArray = new Array<number>(DOC_METRICS_OFFSET).fill(0);
    readyForApprovalMetrics = emptyArray;
    approvedMetrics = emptyArray;
    rejectedMetrics = emptyArray;
    uploadedMetrics = emptyArray;
    expectedMetrics = emptyArray;
    monthsMetrics = [];
    const ownerName = selectedOwner.firstName + ' ' + selectedOwner.lastName;
    const lastMonthsEmptyRecords = docStatusOverTime.getLastMonths(DOC_METRICS_OFFSET);
    const resp = await docService.getDocumentStatusRecords(
      tmfLevel,
      study,
      country === '--' ? '' : country,
      site === '--' ? '' : site,
      selectedDocType?.tmf_document_type,
      selectedDocSubType?.tmf_document_subtype,
      selectedArtifactName?.tmf_artifact_name,
      selectedMilestone.tmf_doc_milestone,
      status,
      ownerName?.trim(),
      DOC_METRICS_OFFSET,
      params
    );
    let maxCountValue = DEFAULT_MAX_RECORDS_COUNT;
    if (resp && resp.data) {
      approvedMetrics = [];
      rejectedMetrics = [];
      uploadedMetrics = [];
      expectedMetrics = [];
      readyForApprovalMetrics = [];
      const responseData = resp.data;
      const arr3: any = mergeArraysByKey(lastMonthsEmptyRecords, responseData);
      for (const metric of arr3) {
        approvedMetrics.push(metric.tmf_doc_approved_count || 0);
        rejectedMetrics.push(metric.tmf_doc_rejected_count || 0);
        uploadedMetrics.push(metric.tmf_doc_uploaded_count || 0);
        expectedMetrics.push(metric.tmf_doc_expected_count || 0);
        readyForApprovalMetrics.push(metric.tmf_doc_Ready_for_approval_count || 0);
        monthsMetrics.push(`${metric.tmf_doc_updated_month}/01/${metric.tmf_doc_updated_year}`);
        const totalDocCount = metric.tmf_doc_total_count;
        if (totalDocCount > maxCountValue) {
          maxCountValue = totalDocCount;
        }
      }
      if (maxCountValue > 100 && maxCountValue < 200) {
        maxCountValue = 200;
      } else if (maxCountValue > 200 && maxCountValue < 400) {
        maxCountValue = 400;
      } else if (maxCountValue > 400 && maxCountValue < 600) {
        maxCountValue = 600;
      } else if (maxCountValue > 600 && maxCountValue < 800) {
        maxCountValue = 800;
      } else if (maxCountValue > 800 && maxCountValue < 1000) {
        maxCountValue = 1000;
      }
      updateChartDetails(maxCountValue);
    } else {
      for (const metric of lastMonthsEmptyRecords.reverse()) {
        monthsMetrics.push(`${metric.tmf_doc_updated_month}/01/${metric.tmf_doc_updated_year}`);
      }
      updateChartDetails(maxCountValue);
    }
  };

  const mergeArraysByKey = (lastMonthsEmptyRecords: any, responseData: any) =>
    lastMonthsEmptyRecords.map((month: any) => ({
      ...responseData.find((item: any) => item.tmf_doc_updated_month === month.tmf_doc_updated_month && item),
      ...month
    }));

  const handleDocTypeChange = (event: SelectChangeEvent) => {
    const selectedDocType: any = event.target.value;
    setSelectedDocType(selectedDocType);
    const tmfDocSubTypes: any = selectedDocType?.tmf_document_subtype || [];
    const docSubTypesList = [...defaultDocSubType, ...tmfDocSubTypes];
    setDocSubTypes(docSubTypesList);
    setSelectedDocSubType(docSubTypesList[0]);
    const tmfArtifacts: any = docSubTypesList[0]?.tmf_artifact || [];
    const docArtifactsList = [...defaultDocArtifactType, ...tmfArtifacts];
    setArtifactNames(docArtifactsList);
    setSelectedArtifactName(docArtifactsList[0]);
  };

  const handleDocSubTypeChange = (event: SelectChangeEvent) => {
    const selectedDocSubTypeValue: any = event.target.value;
    setSelectedDocSubType(selectedDocSubTypeValue);
    const tmfArtifactsArray: any = selectedDocSubTypeValue?.tmf_artifact;
    const docArtifactsList = [...defaultDocArtifactType, ...tmfArtifactsArray];
    setArtifactNames(docArtifactsList);
    setSelectedArtifactName(docArtifactsList[0]);
  };

  const handleArtifactNameChange = (event: SelectChangeEvent) => {
    const selectedArtifactType: any = event.target.value;
    setSelectedArtifactName(selectedArtifactType);
  };

  const handleMilestoneChange = (event: SelectChangeEvent) => {
    setSelectedMilestone(event.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    if (event.target.value.includes('All') && selectedDocStatus.length !== documentsStatusList.length) {
      setSelectedDocStatus(documentsStatusList.map((docStatus: any) => docStatus));
    } else if (event.target.value.includes('All')) {
      setSelectedDocStatus([]);
    } else {
      setSelectedDocStatus(event.target.value);
    }
  };
  const getSelectValues = (selected: any) => {
    if (isAllSelected) {
      return 'All';
    } else
      return documentsStatusList
        .filter((docStatus: any) => selected.includes(docStatus))
        .map((record: any) => record.name)
        .join(', ');
  };

  const handleOwnerChange = (event: SelectChangeEvent) => {
    setSelectedOwner(event.target.value);
  };

  const setDocumentDetailsWithDefaults = () => {
    const docTypesArray = [...defaultDocType, ...documentDetails];
    setDocTypes(docTypesArray);
    setSelectedDocType(docTypesArray[0]);
    const docSubTypesArray = [...defaultDocSubType, ...(docTypesArray[0]?.tmf_document_subtype || [])];
    setDocSubTypes(docSubTypesArray);
    setSelectedDocSubType(docSubTypesArray[0]);
    const docArtifactsArray = [...defaultDocArtifactType, ...(docSubTypesArray[0]?.tmf_artifact || [])];
    setArtifactNames(docArtifactsArray);
    setSelectedArtifactName(docArtifactsArray[0]);
    const milestonesArray = [...defaultMilestone, ...(milestones || [])];
    setMilestones(milestonesArray);
    setSelectedMilestone(milestonesArray[0]);
  };

  const updateChartDetails = (maxCountValue: any) => {
    const chartSeries = [
      { name: 'Approved', data: approvedMetrics },
      { name: 'Ready for Approval', data: readyForApprovalMetrics },
      { name: 'Uploaded', data: uploadedMetrics },
      // { name: 'Expected', data: expectedMetrics },
      { name: 'Rejected', data: rejectedMetrics }
    ];
    setTimelineMetrics(monthsMetrics);
    setDocumentSeriesMetrics(chartSeries);
    setMaxCountValue(maxCountValue);
  };

  useEffect(() => {
    const statusArray = docStatusOverTime.getDocumentStatusList();
    setDocumentsStatusList(statusArray);
    setSelectedDocStatus(statusArray);
    getDocumentDetails();
    getUsersList();
  }, []);

  useEffect(() => {
    if (documentDetails) {
      setDocumentDetailsWithDefaults();
    }
  }, [documentDetails]);

  useEffect(() => {
    if (!userLoading && (study !== '--' || previousStudyValue !== '--')) {
      getMilestoneList();
      getUsersList();
    }
  }, [study, country, site, selectedDocType, selectedDocSubType, selectedArtifactName]);

  useEffect(() => {
    if (!loadingDocStatusOverTime && !masterlistLoading && (study !== '--' || previousStudyValue !== '--')) {
      getDocumentMetricsRecords();
    }
  }, [selectedDocType, selectedDocSubType, selectedArtifactName, selectedMilestone, selectedDocStatus, selectedOwner]);

  return (
    <>
      <h3 style={{ marginTop: '20px', marginBottom: '12px' }}>Document Status Over Time</h3>
      <Grid container sx={{ mb: 1 }} spacing={2}>
        <Grid item md={6} xs={12}>
          <Box className="roundBorderedBox OutlinedVariantForm" sx={{ p: 3, flexGrow: 1, minHeight: 320 }}>
            {documentDetails?.length !== 0 && !loadingDocStatusOverTime && (
              <Grid container direction="row" spacing={3}>
                <Grid item direction="column" md={6} xs={12} columnSpacing={3}>
                  <Grid container direction="row" rowSpacing={3}>
                    <Grid item md={12} sm={12}>
                      <FormControl fullWidth className="customSelect">
                        <InputLabel id="doc-input-type">Document Type</InputLabel>
                        <Select
                          labelId="doc-type-select-label"
                          id="doc-type-select"
                          value={selectedDocType}
                          label="Document Type"
                          onChange={handleDocTypeChange}
                        >
                          {docTypes?.map((docType: any) => (
                            <MenuItem key={docType.zone} value={docType}>
                              {docType.zone} {docType.tmf_document_type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item md={12} sm={12}>
                      <FormControl fullWidth className="customSelect">
                        <InputLabel id="doc-sub-label-type">Document Sub-type</InputLabel>
                        <Select
                          labelId="doc-subtype-labelId"
                          id="doc-labelId-select"
                          value={selectedDocSubType}
                          label="Document Sub-type"
                          onChange={handleDocSubTypeChange}
                        >
                          {docSubTypes?.map((docSubType: any) => (
                            <MenuItem key={docSubType.section} value={docSubType}>
                              {docSubType.section} {docSubType.tmf_document_subtype}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item md={12} sm={12}>
                      <FormControl fullWidth className="customSelect">
                        <InputLabel id="doc-type">Artifact Name</InputLabel>
                        <Select
                          labelId="doc-type"
                          id="doc-type-select"
                          value={selectedArtifactName}
                          label="Artifact Name"
                          onChange={handleArtifactNameChange}
                        >
                          {artifactNames?.map((artifact: any) => (
                            <MenuItem key={artifact.tmf_artifact_number} value={artifact}>
                              {artifact.tmf_artifact_number} {artifact.tmf_artifact_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item md={12} sm={12}>
                      <FormControl fullWidth className="customSelect">
                        <InputLabel id="doc-milestone-type">Milestone</InputLabel>
                        <Select
                          labelId="doc-milestone-label-type"
                          id="doc-milestones-type-select"
                          value={selectedMilestone}
                          label="Milestone"
                          onChange={handleMilestoneChange}
                        >
                          {milestones?.map((milestone: any) => (
                            <MenuItem key={milestone.tmf_doc_milestone} value={milestone}>
                              {milestone.tmf_doc_milestone}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item direction="column" md={6} xs={12} columnSpacing={3}>
                  <Grid container direction="row" rowSpacing={3}>
                    <Grid item md={12} sm={12}>
                      <FormControl fullWidth className="customSelect">
                        <InputLabel id="doc-status-type=label">Status</InputLabel>
                        <Select
                          multiple
                          renderValue={getSelectValues}
                          labelId="doc-status-type"
                          id="doc-status-select"
                          value={selectedDocStatus}
                          label="Status"
                          onChange={handleStatusChange}
                        >
                          <MenuItem key={'All'} value="All">
                            <ListItemIcon>
                              <Checkbox
                                checked={isAllSelected}
                                indeterminate={
                                  selectedDocStatus.length > 0 && selectedDocStatus.length < documentsStatusList.length
                                }
                              />
                            </ListItemIcon>
                            <ListItemText primary="All" />
                          </MenuItem>
                          {documentsStatusList?.map((docStatus: any) => (
                            <MenuItem disabled={selectedDocStatus === null} key={docStatus} value={docStatus}>
                              <Checkbox checked={selectedDocStatus.indexOf(docStatus) > -1} />
                              <ListItemText primary={docStatus.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item md={12} sm={12}>
                      <FormControl fullWidth className="customSelect">
                        <InputLabel id="doc-type">Owner</InputLabel>
                        <Select
                          labelId="doc-type"
                          id="doc-type-select"
                          value={selectedOwner}
                          label="Owner"
                          onChange={handleOwnerChange}
                        >
                          {ownersList?.map((owner: any) => (
                            <MenuItem key={owner.id} value={owner}>
                              {owner.email
                                ? `${owner.firstName} ${owner.lastName}  (${owner.email})`
                                : `${owner.firstName} ${owner.lastName}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {!documentDetails?.length && !loadingDocStatusOverTime && (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No rows
              </Stack>
            )}
            {!documentDetails?.length && loadingDocStatusOverTime && (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Loading...
              </Stack>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <DocumentMetricComponent
            categories={timelineMetrics}
            series={documentSeriesMetrics}
            maxValue={maxCountValue}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default memo(DocumentStatusOverTimeComponent);
